import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Form,
  Spinner,
  Badge,
  InputGroup,
  Alert,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaSearch,
  FaUsers,
  FaCheck,
  FaTimes,
  FaSave,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const StaffBatchAccess = () => {
  const { id } = useParams(); // staff_id
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [staffName, setStaffName] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getAuthToken = () => localStorage.getItem('authToken');

  // 🔹 Fetch staff details and batch access
  useEffect(() => {
    const fetchData = async () => {
      const token = getAuthToken();
      if (!token) {
        setError('Authentication failed.');
        return;
      }

      try {
        const res = await fetch(
          `${appConstants.API_ROOT_URL}get_staff_batch_access?staff_id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const data = await res.json();

        if (res.ok) {
          setBatches(data.data || []);
          setSelectedBatchIds(
            (data.data || []).filter((b) => b.assigned === 1).map((b) => b.id),
          );
          setStaffName(data.staff_name || 'Unknown Staff');
        } else {
          setError(data.message || 'Failed to load batches');
        }
      } catch (error) {
        console.error(error);
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // 🔹 Toggle checkbox selection
  const toggleBatch = (batchId) => {
    setSelectedBatchIds((prev) =>
      prev.includes(batchId)
        ? prev.filter((b) => b !== batchId)
        : [...prev, batchId],
    );
  };

  // 🔹 Save selected access
  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Authentication failed.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${appConstants.API_ROOT_URL}save_staff_batch_access`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            staff_id: id,
            batch_ids: selectedBatchIds,
          }),
        },
      );

      if (res.ok) {
        setSuccess('Batch access updated successfully!');
        setTimeout(() => navigate(-1), 1000);
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to save access');
      }
    } catch (error) {
      console.error(error);
      setError('Error saving access');
    } finally {
      setLoading(false);
    }
  };

  // Filter batches by search
  const filteredBatches = batches.filter(
    (batch) =>
      batch.class_name.toLowerCase().includes(search.toLowerCase()) ||
      batch.division.toLowerCase().includes(search.toLowerCase()),
  );

  // Bulk grant/revoke access
  const handleBulkAction = (grant) => {
    if (grant) {
      setSelectedBatchIds(batches.map((b) => b.id));
    } else {
      setSelectedBatchIds([]);
    }
  };

  return (
    <Layout>
      <div className="container mt-4">
        {/* Top Bar with Search */}
        <div
          style={{
            background: '#343a40',
            color: '#fff',
            borderRadius: 8,
            padding: '0.7rem 1rem 0.7rem 1rem',
            marginBottom: 20,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <FaUsers />
              Batch Access
            </div>
            <div style={{ fontSize: 16, fontWeight: 400, marginTop: 8 }}>
              <span style={{ color: '#ffc107', fontWeight: 500 }}>
                {staffName || '...'}
              </span>
              <span style={{ color: '#adb5bd', marginLeft: 12 }}>
                {selectedBatchIds.length} of {batches.length} batches selected
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <InputGroup
              style={{ minWidth: 260, background: 'white', borderRadius: 8 }}
            >
              <InputGroup.Text
                style={{
                  backgroundColor: 'white',
                  borderRight: 'none',
                  borderRadius: '8px 0 0 8px',
                }}
              >
                <FaSearch style={{ color: '#6c757d' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search batches..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search batches"
                style={{
                  borderLeft: 'none',
                  borderRadius: '0 8px 8px 0',
                  paddingLeft: 0,
                }}
              />
            </InputGroup>
            <Button
              variant="success"
              size="sm"
              style={{ borderRadius: 6, padding: '8px 12px', height: '38px' }}
              onClick={handleSave}
              disabled={loading}
              aria-label="Save"
            >
              {loading ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : (
                <FaSave />
              )}
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              style={{ borderRadius: 6, padding: '8px 12px', height: '38px' }}
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <FaArrowLeft />
            </Button>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="mb-3 d-flex gap-2 justify-content-end">
          <Button
            variant="success"
            size="sm"
            onClick={() => handleBulkAction(true)}
            disabled={loading || batches.length === 0}
            style={{ borderRadius: 6 }}
          >
            <FaCheck className="me-1" /> Select All
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleBulkAction(false)}
            disabled={loading || batches.length === 0}
            style={{ borderRadius: 6 }}
          >
            <FaTimes className="me-1" /> Deselect All
          </Button>
        </div>

        {/* Alert Messages */}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError('')}
            className="mb-3"
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccess('')}
            className="mb-3"
          >
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center mt-5">
            <Spinner animation="border" />
            <p className="mt-2 text-muted">Loading batches...</p>
          </div>
        ) : (
          <>
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflowX: 'auto',
                width: '100%',
              }}
            >
              <Table striped bordered hover responsive className="mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th style={{ width: '60px' }}>#</th>
                    <th style={{ width: '150px' }}>Class</th>
                    <th style={{ width: '150px' }}>Division</th>
                    <th
                      className="text-center"
                      style={{ width: '160px', minWidth: '140px' }}
                    >
                      Can Take Attendance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.length > 0 ? (
                    filteredBatches.map((b, index) => (
                      <tr key={b.id}>
                        <td>{index + 1}</td>
                        <td style={{ fontWeight: 500 }}>{b.class_name}</td>
                        <td>{b.division}</td>
                        <td
                          className="text-center"
                          style={{ verticalAlign: 'middle' }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBatchIds.includes(b.id)}
                            onChange={() => toggleBatch(b.id)}
                            style={{
                              transform: 'scale(1.5)',
                              cursor: 'pointer',
                            }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : search ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No batches found matching your search.
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No batches available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StaffBatchAccess;
